import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import { Location, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'

import { LoadingButton } from '@mui/lab'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, TableContainer } from '@mui/material'

import TextInput from '@/components/ui/inputs/TextInput'
import Pagination from '@/components/ui/pagination/Pagination'

import { useCreateExpertiseMutation } from '@/core/services/expertise'
import { useSearchPatientsQuery } from '@/core/services/patient'

import PatientsTable from '@/pages/patients/ui/PatientsTable'
import { PatientSourceMenu } from '@/components/ui/menu/PatientSourceMenu/PatientSourceMenu'
import { PatientSourceEnum } from '@/core/enums/patient-source.enum'

export function CreateExpertisePage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()

  const background: Location | undefined = _.get(location.state, 'background')

  const [introEnabled, setIntroEnabled] = useState(false)
  const [page, setPage] = useState<number>(1)
  const [patientSource, setPatientSource] = useState(PatientSourceEnum.MedconnectPatient)
  const [searchPatientsArgs, setSearchPatientsArgs] = useState({ patientSource, orderBy: 'userInfo.surname' })
  const [selectedPatient, setSelectedPatient] = useState<string>()

  const {
    isLoading: isFetchingPatients,
    patients,
    totalPatients,
  } = useSearchPatientsQuery(searchPatientsArgs, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      patients:
        data?.items.map((patient) => ({
          _id: patient._id,
          name: patient.userInfo.name,
          surname: patient.userInfo.surname,
          gender: patient.userInfo.gender,
          birthday: patient.userInfo.birthday,
          city: patient.contactInfo?.city,
          secu: patient.secu,
        })) ?? [],
      totalPatients: data?.totalCount ?? 0,
    }),
  })

  const [createExpertise, { isLoading: isCreatingExpertise }] = useCreateExpertiseMutation()

  useEffect(() => {
    if (searchParams.has('patient')) {
      const patientId = searchParams.get('patient')
      if (patientId) setSelectedPatient(patientId)
    }
    // ignore warning in order to run only once after page's first rendering
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (searchParams?.get('intro') === 'true') {
      setIntroEnabled(true)
    }
  }, [searchParams])

  const handleClose = () => {
    navigate(background ? background.pathname : '/')
  }

  const handleChangePage = (newPage: number, rowsPerPage: number) => {
    if (page === newPage) return
    setPage(newPage)
    setSearchPatientsArgs((prevArgs) => ({ ...prevArgs, limit: rowsPerPage, skip: (newPage - 1) * rowsPerPage }))
  }

  const handleSearch = useDebouncedCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchPatientsArgs((prevArgs) => ({ ...prevArgs, smartSearch: event.target.value, skip: 0 }))
    setPage(1)
  }, 300)

  const handleSubmit = async () => {
    if (!selectedPatient) return
    try {
      const expertise = await createExpertise({ patient: selectedPatient }).unwrap()
      navigate(`/expertises/${expertise._id}?intro=${introEnabled}`, { replace: true })
    } catch (error) {}
  }

  return (
    <Dialog disablePortal fullWidth maxWidth="lg" open onClose={handleClose} scroll="paper">
      <DialogTitle>
        <Stack spacing={2}>
          <span>{t('page.createExpertise.title')}</span>
          <Stack direction="row" alignItems="center" spacing={3}>
            <Box flex={1}>
              <TextInput
                id="search-patient"
                fullWidth
                onChange={handleSearch}
                placeholder={t('page.createExpertise.step.patientSelection.searchInput.placeholder')}
              />
            </Box>
            <PatientSourceMenu
              onChange={(patientSource) => {
                setPage(1)
                setPatientSource(patientSource)
                setSearchPatientsArgs((prevArgs) => ({ ...prevArgs, patientSource, skip: 0 }))
              }}
              value={patientSource}
            />
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <TableContainer component={Paper}>
          <PatientsTable
            autoHightlight={false}
            loading={isFetchingPatients}
            data={patients}
            onSelect={(patient) => {
              setSelectedPatient(patient._id)
            }}
            onOpen={(patient) => navigate(`/patients/${patient._id}`)}
          />
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', pt: 2, pl: 0 }}>
        <Pagination color="primary" hideRowsPerPage shape="rounded" page={page} onPageChange={handleChangePage} totalItems={totalPatients} />
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
          <Button onClick={handleClose} variant="neutral">
            {t('common:button.cancel.label')}
          </Button>
          <LoadingButton id="confirmation-submit" disabled={!selectedPatient} loading={isCreatingExpertise} onClick={handleSubmit} variant="contained">
            {t('common:button.submit.label')}
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}

export default CreateExpertisePage
